import {supabase} from '../lib/supabase';

/**
 * Fetches all menu options from the database for a specific product type
 * @param {string} productType - 'burger' or 'pizza'
 * @returns {Promise<Object>} - Object containing all menu options grouped by type
 */
export const fetchMenuOptions=async (productType='burger')=> {
  try {
    console.log(`🔄 Lade ${productType}-Menüoptionen aus der Datenbank...`);
    // FORCE: Clear any cache first
    const {data,error}=await supabase
      .from('menu_options_hb47x')
      .select('*')
      .eq('is_active',true)
      .eq('product_type',productType)
      .order('display_order',{ascending: true});

    if (error) {
      console.error('Error fetching menu options:',error);
      throw error;
    }

    console.log(`✅ ${productType}-Menüoptionen RAW geladen:`,data?.length || 0,'Einträge');
    console.log('RAW DATA:',data);

    // Force refresh - clear any cached data
    if (data && data.length > 0) {
      console.log(`📋 ${productType} Optionen nach Typ:`,data.reduce((acc,item)=> {
        acc[item.option_type]=(acc[item.option_type] || 0) + 1;
        return acc;
      },{}));
    } else {
      console.warn(`⚠️ KEINE ${productType} Optionen gefunden!`);
      // Try to create default data if none exists
      await createDefaultMenuOptions(productType);
      // Retry fetch
      const {data: retryData}=await supabase
        .from('menu_options_hb47x')
        .select('*')
        .eq('is_active',true)
        .eq('product_type',productType)
        .order('display_order',{ascending: true});
      if (retryData && retryData.length > 0) {
        console.log(`✅ Default ${productType} Optionen erstellt und geladen:`,retryData.length);
        return processMenuData(retryData,productType);
      }
    }

    return processMenuData(data,productType);
  } catch (error) {
    console.error('Failed to fetch menu options:',error);
    // Return default values as fallback
    if (productType==='burger') {
      console.log('🔄 Verwende Fallback-Daten für Burger...');
      return {pattyOptions: [],bunOptions: [],toppings: [],sauces: [],spicyLevels: [],sides: []};
    } else {
      console.log('🔄 Verwende Fallback-Daten für Pizza...');
      return {baseOptions: [],toppings: [],sauces: [],spicyLevels: [],sides: []};
    }
  }
};

/**
 * Process raw menu data into structured format
 */
const processMenuData=(data,productType)=> {
  if (!data || data.length===0) {
    console.warn(`⚠️ Keine Daten zum Verarbeiten für ${productType}`);
    return productType==='burger'
      ? {pattyOptions: [],bunOptions: [],toppings: [],sauces: [],spicyLevels: [],sides: []}
      : {baseOptions: [],toppings: [],sauces: [],spicyLevels: [],sides: []};
  }

  // Group data by option_type for burger
  if (productType==='burger') {
    const pattyOptions=data.filter(item=> item.option_type==='patty').map(item=> ({id: item.item_id,name: item.name,price: item.price}));
    const bunOptions=data.filter(item=> item.option_type==='bun').map(item=> ({id: item.item_id,name: item.name,price: item.price}));
    const toppings=data.filter(item=> item.option_type==='topping').map(item=> ({id: item.item_id,name: item.name,price: item.price}));
    const sauces=data.filter(item=> item.option_type==='sauce').map(item=> ({id: item.item_id,name: item.name,price: item.price,isSpicy: item.is_spicy}));
    const spicyLevels=data.filter(item=> item.option_type==='spicy_level').map(item=> ({level: parseInt(item.item_id),name: item.name}));
    const sides=data.filter(item=> item.option_type==='side').map(item=> ({id: item.item_id,name: item.name,price: item.price}));

    const result={pattyOptions,bunOptions,toppings,sauces,spicyLevels,sides};
    console.log(`🍔 Burger-Menü strukturiert:`,{
      pattyOptions: result.pattyOptions.length,
      bunOptions: result.bunOptions.length,
      toppings: result.toppings.length,
      sauces: result.sauces.length,
      spicyLevels: result.spicyLevels.length,
      sides: result.sides.length
    });
    // Debug: Log actual content
    console.log('🔍 PATTY OPTIONS:',result.pattyOptions);
    console.log('🔍 BUN OPTIONS:',result.bunOptions);
    console.log('🔍 TOPPINGS:',result.toppings);
    console.log('🔍 SAUCES:',result.sauces);
    console.log('🔍 SIDES:',result.sides);
    return result;
  }

  // Group data by option_type for pizza
  if (productType==='pizza') {
    const baseOptions=data.filter(item=> item.option_type==='base').map(item=> ({id: item.item_id,name: item.name,price: item.price}));
    const toppings=data.filter(item=> item.option_type==='topping').map(item=> ({id: item.item_id,name: item.name,price: item.price}));
    const sauces=data.filter(item=> item.option_type==='sauce').map(item=> ({id: item.item_id,name: item.name,price: item.price,isSpicy: item.is_spicy}));
    const spicyLevels=data.filter(item=> item.option_type==='spicy_level').map(item=> ({level: parseInt(item.item_id),name: item.name}));
    const sides=data.filter(item=> item.option_type==='side').map(item=> ({id: item.item_id,name: item.name,price: item.price}));

    const result={baseOptions,toppings,sauces,spicyLevels,sides};
    console.log(`🍕 Pizza-Menü strukturiert:`,{
      baseOptions: result.baseOptions.length,
      toppings: result.toppings.length,
      sauces: result.sauces.length,
      spicyLevels: result.spicyLevels.length,
      sides: result.sides.length
    });
    return result;
  }
};


/**
 * Create default menu options if none exist
 */
const createDefaultMenuOptions=async (productType)=> {
  try {
    console.log(`🔧 Erstelle Standard-${productType}-Menü...`);
    if (productType==='burger') {
      const defaultBurgerOptions=[
        // Patties
        {product_type: 'burger',option_type: 'patty',item_id: 'angus',name: 'Angus Rind',price: 18.00,is_active: true,display_order: 1},
        {product_type: 'burger',option_type: 'patty',item_id: 'crispy',name: 'Knuspriges Hähnchen (leicht scharf)',price: 16.00,is_active: true,display_order: 2},
        {product_type: 'burger',option_type: 'patty',item_id: 'classic',name: 'Klassisches Hähnchen',price: 15.00,is_active: true,display_order: 3},
        {product_type: 'burger',option_type: 'patty',item_id: 'vegi',name: 'Vegetarisches Patty',price: 14.00,is_active: true,display_order: 4},
        {product_type: 'burger',option_type: 'patty',item_id: 'beef-classic',name: 'Klassisches Rindfleisch',price: 17.00,is_active: true,display_order: 5},
        // Buns
        {product_type: 'burger',option_type: 'bun',item_id: 'classic-sesame',name: 'Klassisches Sesam-Brötchen',price: 0.00,is_active: true,display_order: 1},
        {product_type: 'burger',option_type: 'bun',item_id: 'no-bun',name: 'Kein Brötchen',price: 0.00,is_active: true,display_order: 2},
        // Toppings
        {product_type: 'burger',option_type: 'topping',item_id: 'cheese-cheddar',name: 'Cheddar Käse',price: 2.00,is_active: true,display_order: 1},
        {product_type: 'burger',option_type: 'topping',item_id: 'lettuce',name: 'Eisbergsalat',price: 1.00,is_active: true,display_order: 2},
        {product_type: 'burger',option_type: 'topping',item_id: 'tomato',name: 'Frische Tomaten',price: 1.00,is_active: true,display_order: 3},
        {product_type: 'burger',option_type: 'topping',item_id: 'cucumber',name: 'Gurken',price: 1.00,is_active: true,display_order: 4},
        {product_type: 'burger',option_type: 'topping',item_id: 'onion-red',name: 'Rote Zwiebeln',price: 1.00,is_active: true,display_order: 5},
        {product_type: 'burger',option_type: 'topping',item_id: 'bacon',name: 'Knuspriger Speck',price: 2.50,is_active: true,display_order: 6},
        {product_type: 'burger',option_type: 'topping',item_id: 'egg-fried',name: 'Spiegelei',price: 2.00,is_active: true,display_order: 7},
        {product_type: 'burger',option_type: 'topping',item_id: 'avocado',name: 'Frische Avocado',price: 3.00,is_active: true,display_order: 8},
        {product_type: 'burger',option_type: 'topping',item_id: 'jalapenos',name: 'Jalapeños',price: 1.50,is_active: true,display_order: 9},
        {product_type: 'burger',option_type: 'topping',item_id: 'pickles',name: 'Essiggurken',price: 1.00,is_active: true,display_order: 10},
        // Sauces
        {product_type: 'burger',option_type: 'sauce',item_id: 'bbq',name: 'BBQ Sauce',price: 1.00,is_spicy: false,is_active: true,display_order: 1},
        {product_type: 'burger',option_type: 'sauce',item_id: 'chili',name: 'Chili Sauce',price: 1.00,is_spicy: true,is_active: true,display_order: 2},
        {product_type: 'burger',option_type: 'sauce',item_id: 'garlic',name: 'Knoblauchsauce',price: 1.00,is_spicy: false,is_active: true,display_order: 3},
        {product_type: 'burger',option_type: 'sauce',item_id: 'mayo',name: 'Mayonnaise',price: 1.00,is_spicy: false,is_active: true,display_order: 4},
        {product_type: 'burger',option_type: 'sauce',item_id: 'ketchup',name: 'Ketchup',price: 1.00,is_spicy: false,is_active: true,display_order: 5},
        // Sides
        {product_type: 'burger',option_type: 'side',item_id: 'fries',name: 'Pommes Frites',price: 5.00,is_active: true,display_order: 1},
        {product_type: 'burger',option_type: 'side',item_id: 'wedges',name: 'Kartoffelecken',price: 6.00,is_active: true,display_order: 2},
        {product_type: 'burger',option_type: 'side',item_id: 'sweet-potato',name: 'Süßkartoffel-Pommes',price: 7.00,is_active: true,display_order: 3},
        {product_type: 'burger',option_type: 'side',item_id: 'salad',name: 'Gemischter Salat',price: 6.00,is_active: true,display_order: 4},
        // Spicy Levels
        {product_type: 'burger',option_type: 'spicy_level',item_id: '1',name: 'Leicht scharf',price: 0.00,is_active: true,display_order: 1},
        {product_type: 'burger',option_type: 'spicy_level',item_id: '2',name: 'Mittel scharf',price: 0.00,is_active: true,display_order: 2},
        {product_type: 'burger',option_type: 'spicy_level',item_id: '3',name: 'Scharf',price: 0.00,is_active: true,display_order: 3},
        {product_type: 'burger',option_type: 'spicy_level',item_id: '4',name: 'Sehr scharf',price: 0.00,is_active: true,display_order: 4},
        {product_type: 'burger',option_type: 'spicy_level',item_id: '5',name: 'Höllenfeuer 🔥',price: 0.00,is_active: true,display_order: 5}
      ];

      const {error}=await supabase
        .from('menu_options_hb47x')
        .insert(defaultBurgerOptions);

      if (error) {
        console.error('Error creating default burger options:',error);
      } else {
        console.log('✅ Standard Burger-Optionen erstellt');
      }
    }
  } catch (error) {
    console.error('Error creating default menu options:',error);
  }
};


/**
 * Force refresh menu options by clearing cache and reloading
 * @param {string} productType - 'burger' or 'pizza'
 * @returns {Promise<Object>} - Fresh menu options
 */
export const refreshMenuOptions=async (productType='burger')=> {
  console.log(`🔄 FORCE REFRESH: ${productType}-Menü wird neu geladen...`);
  // Add a small delay to ensure database operations are complete
  await new Promise(resolve=> setTimeout(resolve,500));
  return await fetchMenuOptions(productType);
};

/**
 * Test database connection and data availability
 * @returns {Promise<Object>} - Test results
 */
export const testMenuDatabase=async ()=> {
  try {
    console.log('🧪 Teste Menü-Datenbank...');
    const {data: burgerData,error: burgerError}=await supabase
      .from('menu_options_hb47x')
      .select('*')
      .eq('product_type','burger')
      .limit(5);
    const {data: pizzaData,error: pizzaError}=await supabase
      .from('menu_options_hb47x')
      .select('*')
      .eq('product_type','pizza')
      .limit(5);

    console.log('🧪 Burger Test Daten:',burgerData);
    console.log('🧪 Pizza Test Daten:',pizzaData);

    return {
      burger: {
        available: !burgerError,
        count: burgerData?.length || 0,
        error: burgerError?.message
      },
      pizza: {
        available: !pizzaError,
        count: pizzaData?.length || 0,
        error: pizzaError?.message
      }
    }
  } catch (error) {
    console.error('Database test failed:',error);
    return {
      burger: {available: false,count: 0,error: error.message},
      pizza: {available: false,count: 0,error: error.message}
    };
  }
}